<template>
  <Card class="page-summary-card clickable">
    <div class="card-block" v-on:click="openCardPreview">
      <div class="row">
        <div class="col-12">
          <p class="typography--body-3">
            <strong class="typography--color-secondary">{{ index + 1 }}</strong>
            <span class="typography--color-disabled ml-1" v-if="page.is_quiz">{{ $t('common.card.quiz') }}</span>
            <span class="typography--color-disabled ml-1" v-else>{{ $t('common.card.non_quiz') }}</span>
          </p>
          <div v-if="this.isPublisherUser" v-bind:style="{ background: getPageMetadataBackgroundColor, cursor: 'default' }">
            <div class="ml-1">[페이지] - {{ this.page.id }} ({{ getPageShareLevelDescription }})</div>
            <div class="ml-1" v-if="hasExtraContent">* 긴지문 있음 *</div>
            <div class="ml-1">[핵심개념] - {{ this.page.core_concept_id }} ({{ getCoreConceptDescription }})</div>
            <div class="ml-1">[키워드]</div>
            <div v-for="(keyword, index) in getKeywords" :key="index">
              {{ `${keyword.id}: ${keyword.name}` }}
            </div>
          </div>
          <div class="page-content-container"
               v-bind:class="classBindings">
            <div class="page-image-summary float-left" v-if="hasExtraContent" v-bind:style="{ 'background-image': extraContentImageSrc }"></div>
            <div class="page-image-summary float-left" v-if="hasImage" v-bind:style="{ 'background-image': firstImageSrc }"></div>

            <div class="page-audio-summary" v-if="hasAudio">
              <img src="/static/images/icon-audio.svg" class="align-middle" />
              <span class="align-middle typography--body-3">{{ firstAudioDuration | duration }}</span>
            </div>

            <div class="page-video-summary" v-if="hasVideo || hasEmbededVideo">
              <img src="/static/images/icon-video.svg" class="align-middle" />
              <span class="align-middle typography--body-3">{{ firstVideoDuration | duration }}</span>
            </div>

            <div class="typography--body-2 typography--pre-line page-content"
                v-html="summarizedContent">
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="card-action">
      <div class="card-action-btn">
        <IconDropdown>
          <b-dropdown-item class="dropdown-item"
                           v-if="isEditablePage"
                           v-on:click="$emit('edit-page', index)">
            {{ $t('common.card.page.edit') }}
          </b-dropdown-item>
          <b-dropdown-item class="dropdown-item" v-on:click="$emit('remove-page', index)">
            {{ $t('common.card.page.remove') }}
          </b-dropdown-item>
          <div v-if="this.isPublisherUser">
            <div class="dropdown-divider"></div>
            <div class="dropdown-label" v-bind:style="{'padding-left': '20px'}">
              <strong>관리자 메뉴</strong>
            </div>
            <b-dropdown-item v-if="this.page.share_level !== 99" class="dropdown-item" v-on:click="$emit('hidden-page', index)">
              숨김
            </b-dropdown-item>
            <b-dropdown-item v-else class="dropdown-item" v-on:click="$emit('recover-hidden-page', index)">
              숨김처리 복구
            </b-dropdown-item>
            <b-dropdown-item class="dropdown-item" v-on:click="$emit('edit-extra-content', index)">
              긴지문 설정
            </b-dropdown-item>
          </div>
        </IconDropdown>
      </div>
    </div>
  </Card>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { get, map, isEmpty } from 'lodash';

  export default {
    name: 'my-page-summary',

    props: [
      'card-title',
      'index',
      'page',
      'card',
    ],

    computed: {
      ...mapGetters([
        'currentPublisherInfo',
        'isPublisherUser',
        'currentUser',
        'coreConcepts',
      ]),

      isEditablePage() {
        return this.page && this.page.author_id === this.currentUser.id &&
          (!this.page.is_copied_page ||
           (this.isPublisherUser &&
            this.currentPublisherInfo.user_id === this.page.author_id &&
            this.currentPublisherInfo.id === this.page.publisher_id));
      },

      getCoreConceptDescription() {
        const coreConceptName = get(this.coreConcepts[this.page.core_concept_id], 'name', '');
        return coreConceptName;
      },

      getPageMetadataBackgroundColor() {
        if (this.card && this.card.core_concept_id !== this.page.core_concept_id) {
          return '#ff0000aa';
        }
        if (this.page.share_level === 1) {
          return '#00ff0060';
        }
        if (this.page.share_level === 99) {
          return '#0000ff60';
        }
        return '#00000015';
      },

      getPageShareLevelDescription() {
        if (this.page.share_level === 1) {
          return '전체 공개';
        }
        if (this.page.share_level === 2) {
          return '비공개';
        }
        if (this.page.share_level === 3) {
          return '기관 공개';
        }
        if (this.page.share_level === 99) {
          return '숨김';
        }
        return this.page.share_level;
      },

      getKeywords() {
        return map(this.page.page_keywords, 'keyword');
      },

      pageContent() {
        return this.page ? this.page.content : '<p></p>';
      },

      summarizedContent() {
        return this.pageContent.replace(/<iframe.*<\/iframe>/g, '');
      },

      contentImages() {
        return this.page.images.filter(image => (
          this.page.content.indexOf(`data-image-id="${this.getImageId(image)}"`) !== -1
        ));
      },

      hasImage() {
        return this.page.image_count > 0 && this.contentImages.length > 0;
      },

      hasAudio() {
        return this.page.audio_count > 0 && this.page.audios.length > 0;
      },

      hasVideo() {
        return this.page.video_count > 0 && this.page.videos && this.page.videos.length > 0;
      },

      hasEmbededVideo() {
        return this.page.content.match(/<iframe/g) || (this.page.description || '').match(/<iframe/g);
      },

      classBindings() {
        return {
          'has-image': this.hasImage,
          'has-audio': this.hasAudio,
          'has-video': this.hasVideo || this.hasEmbededVideo,
        };
      },

      extraContentImageSrc() {
        if (this.page.extra_content.image_url) {
          return `url('${this.page.extra_content.image_url}')`;
        }
        return '';
      },

      firstImageSrc() {
        if (this.hasImage) {
          const imageUrl = this.contentImages[0].image_url ||
            this.contentImages[0].small_url ||
            this.contentImages[0].medium_url ||
            this.contentImages[0].original_url;

          return `url('${imageUrl}')`;
        }

        return '';
      },

      firstAudioDuration() {
        if (this.hasAudio) {
          return this.page.audios[0].duration;
        }

        return 0;
      },

      firstVideoDuration() {
        if (this.hasVideo) {
          return this.page.videos[0].duration;
        }

        return 0;
      },

      hasExtraContent() {
        return !isEmpty(this.page.extra_content);
      },
    },

    methods: {
      ...mapActions([
        'setSelectedPreviewPageIndex',
        'setSelectedPreviewCard',
        'setIsPagePreview',
      ]),

      getImageId(image) {
        return image.id || image.key;
      },

      openCardPreview() {
        const tempCard = {
          title: this.cardTitle,
          pages: [this.page],
        };

        this.setSelectedPreviewCard(tempCard)
          .then(() => (
            this.setSelectedPreviewPageIndex(0)
          ))
          .then(() => (
            this.setIsPagePreview(true)
          ))
          .then(() => {
            this.$root.$emit('show::modal', 'card-preview-modal');
          });
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/app/my-cards/components/MyPageSummary.vue